// Sends one queued photo to `POST /api/projects/:projectId/uploads`. XMLHttpRequest rather
// than fetch, because only XHR reports upload progress. Never throws: every outcome is a
// SendResult (`classifyResponse` in upload-queue.ts decides which).

import {
  classifyResponse,
  type QueuedUpload,
  type SendResult,
  uploadFields,
} from "@/lib/upload-queue";

/** No upload progress for this long means the connection has stalled. */
const STALL_MS = 60_000;
/** Once every byte is up, how long the server may take to store and answer. */
const ANSWER_MS = 120_000;

export function sendUpload(
  item: QueuedUpload,
  onProgress: (fraction: number) => void,
  signal?: AbortSignal,
): Promise<SendResult> {
  return new Promise<SendResult>((resolve) => {
    const form = new FormData();
    form.append("file", item.blob, item.fileName);
    for (const [name, value] of uploadFields(item)) form.append(name, value);

    const xhr = new XMLHttpRequest();
    let timer: ReturnType<typeof setTimeout> | undefined;
    let settled = false;

    const finish = (result: SendResult) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      resolve(result);
    };
    const watch = (ms: number, error: string) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        // Finish first: abort() fires onabort synchronously, which would win with its message.
        finish({ kind: "retry", error });
        xhr.abort();
      }, ms);
    };
    const onAbort = () => {
      finish({ kind: "retry", error: "Upload cancelled" });
      xhr.abort();
    };

    if (signal?.aborted) {
      finish({ kind: "retry", error: "Upload cancelled" });
      return;
    }
    signal?.addEventListener("abort", onAbort);

    xhr.open("POST", `/api/projects/${encodeURIComponent(item.projectId)}/uploads`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
      watch(STALL_MS, "The connection stalled");
    };
    xhr.upload.onload = () => {
      onProgress(1);
      watch(ANSWER_MS, "The server took too long to answer");
    };
    xhr.onload = () => finish(classifyResponse(xhr.status, xhr.responseText));
    xhr.onerror = () => finish(classifyResponse(0, ""));
    xhr.onabort = () => finish({ kind: "retry", error: "Upload cancelled" });

    watch(STALL_MS, "The connection stalled");
    xhr.send(form);
  });
}
